import React, { useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MenuSections } from '../../../types/menu-sections';

import { CreativityType } from '../../../types/banner';
import { useTemplateConfig } from '../../../hooks/banners/use-template-config';
import { CreativityForm } from '../../../components/form/creativity-form';

import { EditorDataProvider } from '../context/editor-data-provider';
import { useEditorData } from '../hooks/use-editor-data';
import { CreativityEditor, type ResolutionConfig } from '@doer/frontend-components/creativity-editor';
import { SaveProgressModal } from '../components/save-progress-modal';
import { LoadingScreen } from '@doer/frontend-components/spinner';
import { useProjectActions } from '../hooks/use-project-actions';
import { getHandlerForTemplate } from '../../../handlers';
import { useSubmitEditorBanner } from '../hooks/use-submit-editor-banner';
import { useReopenPiece } from '../hooks/use-reopen-piece';
import { useBulkEditorBridge } from '../hooks/use-bulk-editor-bridge';
import { BrandsAudioServicesProvider } from '../context/brands-audio-services-provider';
import { useClientId } from '../../../hooks/use-client-id';
import { getBrandKit } from '../../../clients/brand-kit-registry';

const EditorPageInner: React.FC = () => {
  const navigate = useNavigate();
  const clientId = useClientId();
  const brandKit = React.useMemo(() => getBrandKit(clientId), [clientId]);

  const {
    creativityType,
    formData,
    errors,
    fields,
    isFormComplete,
    handleFieldChange,
    setFieldError,
    canvas,
    contentReady,
    editorRef,
    onEditorReady,
    onLoadingChange,
    resolutions,
    activeResolution,
    setActiveResolutionId,
    layerLabelResolver,
    resolveResolutionJson
  } = useEditorData();

  const { handleSaveAsNewProject } = useProjectActions({
    width: activeResolution.canvasWidth,
    height: activeResolution.canvasHeight
  });

  const onNavigate = useCallback((section: MenuSections) => navigate(`/${section}`), [navigate]);

  const { handleSaveToLibrary, isSaving, saveProgress } = useSubmitEditorBanner({ onNavigate });

  useBulkEditorBridge({
    creativityType,
    formData,
    isFormComplete,
    isSaving,
    handleFieldChange,
    handleSaveToLibrary
  });

  const getExportFileName = useCallback(
    (resolution: ResolutionConfig) => {
      const handler = getHandlerForTemplate(creativityType);
      const customName = handler.getFileNaming?.({ ...formData, _creativityType: creativityType }, resolution);
      return customName ?? `export-${resolution.id}.png`;
    },
    [creativityType, formData]
  );

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%'
      }}
    >
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', backgroundColor: '#F0F0F0' }}>
        <div
          style={{
            width: '30%',
            minWidth: 340,
            maxWidth: 580,
            flexShrink: 0,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#F0F0F0'
          }}
        >
          <div
            style={{
              flex: 1,
              borderRadius: 16,
              display: 'flex',
              flexDirection: 'column',
              padding: 12,
              backgroundColor: 'white',
              overflowY: 'auto'
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                flex: 1,
                overflowY: 'auto',
                scrollbarColor: '#e3e3e3 #FFFFFF',
                borderRadius: 12
              }}
            >
              {canvas && (
                <CreativityForm
                  creativityType={creativityType}
                  formData={formData}
                  errors={errors}
                  fields={fields}
                  isFormComplete={isFormComplete}
                  onFieldChange={handleFieldChange}
                  setFieldError={setFieldError}
                  onSubmit={() => undefined}
                  showSubmit={false}
                  activeResolutionId={activeResolution.id}
                />
              )}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <CreativityEditor
            ref={editorRef}
            kind='form-editor'
            host='creativity'
            brandKit={brandKit}
            resolutions={resolutions}
            activeResolutionId={activeResolution.id}
            onResolutionChange={setActiveResolutionId}
            onReady={onEditorReady}
            onLoadingChange={onLoadingChange}
            isSaving={isSaving}
            layerLabelResolver={layerLabelResolver}
            resolveResolutionJson={resolveResolutionJson}
            getExportFileName={getExportFileName}
            onSave={handleSaveToLibrary}
            onSaveAs={handleSaveAsNewProject}
          />
        </div>
      </div>

      {!contentReady && <LoadingScreen label='Cargando pieza…' overlay />}

      <SaveProgressModal progress={saveProgress} />
    </div>
  );
};

export const BindingCreativityEditorPage: React.FC = () => {
  const { creativityType, contentId } = useParams<{ creativityType: string; contentId?: string }>();
  const navigate = useNavigate();
  const template = useTemplateConfig(creativityType as CreativityType);
  const canvasBinding = template?.mode === 'wysiwyg' ? template.canvasBinding : undefined;
  const { loading, initialFormData, savedModels } = useReopenPiece(contentId);

  useEffect(() => {
    if (template && !canvasBinding) {
      navigate(`/creativity-form/${creativityType}`, { replace: true });
    }
  }, [creativityType, template, canvasBinding, navigate]);

  if (!creativityType || !canvasBinding) {
    return null;
  }

  if (loading) {
    return <LoadingScreen label='Cargando pieza…' />;
  }

  return (
    <BrandsAudioServicesProvider>
      <EditorDataProvider
        creativityType={creativityType as CreativityType}
        canvasBinding={canvasBinding}
        contentId={contentId}
        initialFormData={initialFormData}
        savedModels={savedModels}
      >
        <EditorPageInner />
      </EditorDataProvider>
    </BrandsAudioServicesProvider>
  );
};
